"use client";

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LanguageAlphabet, AlphabetLetter } from "@/lib/dialog-data";
import type { LocaleCode } from "@/lib/api/client";
import { useTTS } from "@/lib/use-tts";
import { useLanguage } from "@/lib/language-context";
import { useTranslations } from "@/lib/use-translations";
import { SpeakerButton } from "./speaker-button";

interface AlphabetSectionProps {
  alphabet: LanguageAlphabet;
}

function LetterCard({ letter, targetLang }: { letter: AlphabetLetter; targetLang: LocaleCode }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [speakingText, setSpeakingText] = useState<string | null>(null);
  const { speak, stop, state: ttsState } = useTTS({ lang: targetLang });
  const t = useTranslations();

  // Clear speaking text when TTS stops
  useEffect(() => {
    if (ttsState === "idle" && speakingText) {
      setSpeakingText(null);
    }
  }, [ttsState, speakingText]);

  const handleSpeak = (text: string) => {
    if (ttsState === "playing" && speakingText === text) {
      stop();
    } else {
      setSpeakingText(text);
      speak(text);
    }
  };

  const getStateForText = (text: string) =>
    speakingText === text ? ttsState : "idle";

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
      >
        {/* Letter */}
        <div className="min-w-12 h-12 px-2 flex items-center justify-center bg-primary/10 rounded-lg shrink-0">
          <span className={cn(
            "font-bold text-primary",
            letter.letter.length <= 2 ? "text-2xl" : "text-xl"
          )}>{letter.letter}</span>
        </div>

        {/* Name and Sound */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{letter.name}</span>
            <SpeakerButton
              state={getStateForText(letter.name)}
              onClick={(e) => {
                e.stopPropagation();
                handleSpeak(letter.name);
              }}
              size="sm"
            />
          </div>
          <p className="text-sm text-muted-foreground truncate">{letter.sound}</p>
        </div>

        {/* Expand Icon */}
        <div className="shrink-0">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
          {/* Sound Description */}
          <div className="bg-muted/50 rounded-md p-2">
            <p className="text-sm text-foreground">{letter.sound}</p>
          </div>

          {/* Examples */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.alphabet.examples}
            </h4>
            {letter.examples.map((example, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-background rounded-md p-2"
              >
                <SpeakerButton
                  state={getStateForText(example.word)}
                  onClick={() => handleSpeak(example.word)}
                  className="bg-primary/10 hover:bg-primary/20"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-foreground">{example.word}</span>
                    <span className="text-xs text-muted-foreground">
                      [{example.pronunciation}]
                    </span>
                  </div>
                  <p className="text-sm text-primary">{example.translation}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          {letter.notes && (
            <div className="flex gap-2 bg-accent/10 rounded-md p-2">
              <Info className="w-4 h-4 text-accent shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{letter.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AlphabetSection({ alphabet }: AlphabetSectionProps) {
  const [activeSection, setActiveSection] = useState<number | "all">("all");
  const { targetLanguage } = useLanguage();
  const t = useTranslations();

  // Get all letters sorted: single characters first (alphabetically), then multi-character (diphthongs/digraphs)
  const getAllLettersSorted = () => {
    const allLetters = alphabet.sections.flatMap(section => section.letters);

    // Separate single characters from multi-character combinations
    const singleChars = allLetters.filter(l => l.letter.length === 1);
    const multiChars = allLetters.filter(l => l.letter.length > 1);

    // Sort each group alphabetically (case-insensitive)
    const sortAlpha = (a: AlphabetLetter, b: AlphabetLetter) =>
      a.letter.toLowerCase().localeCompare(b.letter.toLowerCase());

    singleChars.sort(sortAlpha);
    multiChars.sort(sortAlpha);

    return [...singleChars, ...multiChars];
  };

  const currentLetters = activeSection === "all"
    ? getAllLettersSorted()
    : alphabet.sections[activeSection]!.letters;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Section Tabs */}
      <div className="sticky top-0 bg-background z-10 shadow-[0_4px_16px_rgba(255,255,255,0.9)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
        <div className="flex gap-1 p-2 overflow-x-auto">
          {/* All button */}
          <button
            type="button"
            onClick={() => setActiveSection("all")}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors",
              activeSection === "all"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {t.alphabet.all}
          </button>
          {alphabet.sections.map((section, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveSection(idx)}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors",
                activeSection === idx
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Section Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Section Description (only for specific sections, not "all") */}
          {activeSection !== "all" && alphabet.sections[activeSection]?.description && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground">
                {alphabet.sections[activeSection]!.description}
              </p>
            </div>
          )}

          {/* Letters Grid */}
          <div className="grid gap-2">
            {currentLetters.map((letter, idx) => (
              <LetterCard key={`${letter.letter}-${idx}`} letter={letter} targetLang={targetLanguage.code} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
